import type { CommunityDetail } from '@pba/react-network';
import { useCommunityQuery } from '@pba/react-network';
import { useCallback, useState } from 'react';
import { usePermissions } from 'permissions';
import type { ModalOptions } from 'ui';
import { useUser } from 'features/auth';
import { ConsentsModal } from 'features/consents';
import { useJoinCommunityMutation } from '../../queries';
import { RequestJoinModal, type RequestJoinModalOptions } from '../request-join-modal';
import { JoinButton } from './join-button';

type P = {
  community: CommunityDetail;
};

const CommunityJoinButtons = ({ community }: P) => {
  const { railsUserData: viewer } = useUser();
  const [isConsentsModalOpened, setIsConsentsModalOpened] = useState(false);
  const [requestJoinOptions, setRequestJoinOptions] = useState<ModalOptions<RequestJoinModalOptions>>({
    isOpened: false,
  });

  const { hasMembershipInCommunity } = usePermissions();
  const isMemberInCommunity = hasMembershipInCommunity({ communityId: community.id });
  const isRequestPending = community.changeRequests.some(
    (request) => request.kind === 'community_request_to_join' && request.status === 'pending',
  );

  const { isRefetching: isReloadingCommunity } = useCommunityQuery({ filter: {}, id: community.id });
  const { isPending: isJoiningCommunity, mutate: joinCommunity } = useJoinCommunityMutation();

  const onJoin = useCallback(() => {
    joinCommunity({ id: community.id });
  }, [community.id, joinCommunity]);

  const onOpenConsentsModal = useCallback(() => {
    setIsConsentsModalOpened(true);
  }, []);

  const onOpenRequestJoinModal = useCallback(() => {
    setRequestJoinOptions({ community, isOpened: true });
  }, [community]);

  const closeConsentsModal = useCallback(() => {
    setIsConsentsModalOpened(false);
  }, []);

  const closeRequestJoinModal = useCallback(() => {
    setRequestJoinOptions({ isOpened: false });
  }, []);

  if (isMemberInCommunity) {
    return null;
  }

  return (
    <>
      <JoinButton
        community={community}
        isLoading={isReloadingCommunity || isJoiningCommunity}
        isRequestPending={isRequestPending}
        onJoin={onJoin}
        onOpenConsentsModal={onOpenConsentsModal}
        onOpenRequestJoinModal={onOpenRequestJoinModal}
        usersConsents={viewer.usersConsents}
      />
      <ConsentsModal
        entity={community}
        isOpened={isConsentsModalOpened}
        onAccept={closeConsentsModal}
        onClose={closeConsentsModal}
        onReject={closeConsentsModal}
        requiredConsentIds={community.organization.requiredConsents.map((consent) => consent.id)}
      />
      <RequestJoinModal
        onCancel={closeRequestJoinModal}
        onSuccess={closeRequestJoinModal}
        options={requestJoinOptions}
      />
    </>
  );
};

export default CommunityJoinButtons;
